import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { X, ArrowRight, BarChart3, ChevronUp, ChevronDown } from 'lucide-react';
import { Button } from '../ui/button';

/**
 * StickyCompareBar - Shows a sticky bar at the bottom when cities are selected for comparison
 * 
 * Usage: Add to App.js or a layout component
 * The bar reads from localStorage to track selected cities across pages
 * 
 * IMPORTANT: This bar should NOT appear on:
 * - /compare/* routes (user is already viewing a comparison)
 * - /compare-country/* routes
 * 
 * It should only appear on pages where selecting/comparing cities makes sense:
 * - /cities, /countries, /move-to/*, city detail pages, etc.
 */
export default function StickyCompareBar() {
  const [selectedCities, setSelectedCities] = useState([]);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const location = useLocation();

  // Check if current route is a comparison page where the bar should be hidden
  const isComparisonPage = 
    location.pathname.startsWith('/compare/') || 
    location.pathname.startsWith('/compare-country/') ||
    location.pathname === '/compare' ||
    location.pathname === '/tools/city-comparison' ||
    location.pathname === '/tools/country-comparison';

  // Listen for city selection changes via custom event or localStorage
  useEffect(() => {
    const checkSelectedCities = () => {
      const stored = localStorage.getItem('compareCities');
      if (stored) {
        try {
          const cities = JSON.parse(stored);
          if (Array.isArray(cities) && cities.length >= 2) {
            setSelectedCities(cities);
            setIsVisible(true);
          } else {
            setIsVisible(false);
          }
        } catch {
          setIsVisible(false);
        }
      } else {
        setIsVisible(false);
      }
    };

    // Initial check
    checkSelectedCities();

    // Listen for storage changes (cross-tab) and custom events (same tab)
    window.addEventListener('storage', checkSelectedCities);
    window.addEventListener('compareCitiesUpdated', checkSelectedCities);

    return () => {
      window.removeEventListener('storage', checkSelectedCities);
      window.removeEventListener('compareCitiesUpdated', checkSelectedCities);
    };
  }, []);

  const handleClear = () => {
    localStorage.removeItem('compareCities');
    setSelectedCities([]);
    setIsVisible(false);
    window.dispatchEvent(new Event('compareCitiesUpdated'));
  };

  // Don't show on comparison pages or if no cities selected
  if (!isVisible || selectedCities.length < 2 || isComparisonPage) {
    return null;
  }

  const [cityA, cityB] = selectedCities;
  const compareUrl = `/compare/${cityA.slug}-vs-${cityB.slug}`;

  return (
    <div 
      className={`fixed bottom-0 left-0 right-0 z-50 transition-transform duration-300 ${
        isCollapsed ? 'translate-y-[calc(100%-40px)]' : 'translate-y-0'
      }`}
      data-testid="sticky-compare-bar"
    >
      {/* Collapse Toggle Button */}
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="absolute -top-8 left-1/2 -translate-x-1/2 bg-white border border-b-0 border-slate-200 rounded-t-lg px-4 py-1 text-slate-500 hover:text-slate-700 transition-colors shadow-sm md:hidden"
        aria-label={isCollapsed ? 'Expand compare bar' : 'Collapse compare bar'}
      >
        {isCollapsed ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>

      {/* Main Bar */}
      <div className="bg-white border-t border-slate-200 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between gap-4">
            {/* City Comparison Info */}
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="hidden sm:flex items-center justify-center w-10 h-10 rounded-full bg-blue-100">
                <BarChart3 className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-sm text-slate-500 hidden sm:inline">Comparing:</span>
                <div className="flex items-center gap-2 font-medium text-slate-900 truncate">
                  <span className="truncate">{cityA.name}</span>
                  <ArrowRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
                  <span className="truncate">{cityB.name}</span>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <Link to={compareUrl}>
                <Button 
                  size="sm" 
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="view-comparison-btn"
                >
                  <span className="hidden sm:inline">View Comparison</span>
                  <span className="sm:hidden">Compare</span>
                  <ArrowRight className="ml-1 w-4 h-4" />
                </Button>
              </Link>
              <button
                onClick={handleClear}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                aria-label="Clear comparison"
                data-testid="clear-comparison-btn"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Helper function to add cities to comparison (call from city cards/pages)
export function addCityToCompare(city) {
  const stored = localStorage.getItem('compareCities');
  let cities = [];
  
  try {
    cities = stored ? JSON.parse(stored) : [];
  } catch {
    cities = [];
  }

  // Check if city already exists
  if (cities.find(c => c.slug === city.slug)) {
    return false;
  }

  // Add city (max 2)
  if (cities.length >= 2) {
    cities = [cities[1], city]; // Replace first city, keep second
  } else {
    cities.push(city);
  }

  localStorage.setItem('compareCities', JSON.stringify(cities));
  window.dispatchEvent(new Event('compareCitiesUpdated'));
  return true;
}

// Helper function to remove city from comparison
export function removeCityFromCompare(citySlug) {
  const stored = localStorage.getItem('compareCities');
  if (!stored) return;

  try {
    const cities = JSON.parse(stored).filter(c => c.slug !== citySlug);
    localStorage.setItem('compareCities', JSON.stringify(cities));
    window.dispatchEvent(new Event('compareCitiesUpdated'));
  } catch {
    // Ignore errors
  }
}

// Helper function to check if city is in comparison
export function isCityInCompare(citySlug) {
  const stored = localStorage.getItem('compareCities');
  if (!stored) return false;

  try {
    const cities = JSON.parse(stored);
    return cities.some(c => c.slug === citySlug);
  } catch {
    return false;
  }
}
